/**
 * SELECTOR BUSCABLE (FASE 9.2 — UX-DEBT-SELECTORES-BUSCABLES)
 *
 * Un único componente para elegir Artículo, Almacén… en catálogos que no
 * caben en un <select>. Patrón WAI-ARIA «combobox» con lista emergente:
 *   · input role=combobox, aria-expanded, aria-controls, aria-autocomplete=list,
 *     aria-activedescendant → opción resaltada;
 *   · listbox con role=option y aria-selected;
 *   · teclado: ↓/↑ recorren, Inicio/Fin, Intro elige, Escape cierra (y si
 *     ya está cerrada, vuelve al valor elegido), Tab sale sin elegir.
 * La búsqueda es EN SERVIDOR (paginada/acotada y por ámbito: la RLS decide
 * qué existe para quien pregunta); aquí sólo se espera a que el usuario deje
 * de teclear. Sin dependencias externas. En móvil la lista ocupa el ancho
 * del campo y cada opción mide ≥ 44 px.
 */
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

export interface PropsBuscador<T> {
  readonly etiqueta: string;
  readonly valor: T | null;
  readonly alElegir: (v: T | null) => void;
  /** Búsqueda en servidor; recibe el texto tecleado ('' = primeras opciones). */
  readonly buscar: (texto: string) => Promise<readonly T[]>;
  readonly clave: (v: T) => string;
  readonly texto: (v: T) => string;
  readonly detalle?: ((v: T) => ReactNode) | undefined;
  readonly placeholder?: string | undefined;
  readonly deshabilitado?: boolean | undefined;
  readonly error?: string | undefined;
  /** Cambia → se descartan los resultados (p. ej. el almacén del que dependen los artículos). */
  readonly contexto?: string | undefined;
  readonly sinResultados?: string | undefined;
  readonly espera?: number | undefined;
}

export function Buscador<T>({
  etiqueta, valor, alElegir, buscar, clave, texto, detalle, placeholder = 'Escribe para buscar…', deshabilitado = false,
  error, contexto = '', sinResultados = 'Sin resultados.', espera = 200,
}: PropsBuscador<T>): ReactNode {
  const id = useId();
  const idLista = `${id}-lista`;
  const idError = `${id}-error`;
  const [abierto, setAbierto] = useState(false);
  const [consulta, setConsulta] = useState(valor === null ? '' : texto(valor));
  const [opciones, setOpciones] = useState<readonly T[]>([]);
  const [activa, setActiva] = useState(-1);
  const [cargando, setCargando] = useState(false);
  const [fallo, setFallo] = useState(false);
  const turno = useRef(0);
  const raiz = useRef<HTMLDivElement>(null);

  //  El valor elegido manda sobre el texto cuando cambia desde fuera.
  const claveValor = valor === null ? '' : clave(valor);
  useEffect(() => {
    setConsulta(valor === null ? '' : texto(valor));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claveValor]);
  useEffect(() => { setOpciones([]); }, [contexto]);

  useEffect(() => {
    if (!abierto) return undefined;
    const mio = ++turno.current;
    const filtro = valor !== null && consulta === texto(valor) ? '' : consulta.trim();
    setCargando(true);
    const t = setTimeout(() => {
      buscar(filtro).then((r) => {
        if (mio !== turno.current) return;
        setOpciones(r);
        setFallo(false);
        setActiva(r.length > 0 ? 0 : -1);
      }).catch(() => {
        if (mio === turno.current) { setOpciones([]); setFallo(true); }
      }).finally(() => {
        if (mio === turno.current) setCargando(false);
      });
    }, espera);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto, consulta, contexto]);

  //  Clic fuera: cerrar y restaurar el texto del valor elegido.
  useEffect(() => {
    if (!abierto) return undefined;
    function fuera(e: MouseEvent): void {
      if (raiz.current !== null && !raiz.current.contains(e.target as Node)) cerrar();
    }
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  });

  function cerrar(): void {
    setAbierto(false);
    setActiva(-1);
    setConsulta(valor === null ? '' : texto(valor));
  }
  function elegir(v: T): void {
    alElegir(v);
    setConsulta(texto(v));
    setAbierto(false);
    setActiva(-1);
  }
  function teclear(e: KeyboardEvent<HTMLInputElement>): void {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!abierto) { setAbierto(true); return; }
        setActiva((a) => Math.min(opciones.length - 1, a + 1));
        return;
      case 'ArrowUp':
        e.preventDefault();
        setActiva((a) => Math.max(0, a - 1));
        return;
      case 'Home':
        if (abierto && opciones.length > 0) { e.preventDefault(); setActiva(0); }
        return;
      case 'End':
        if (abierto && opciones.length > 0) { e.preventDefault(); setActiva(opciones.length - 1); }
        return;
      case 'Enter':
        if (abierto && activa >= 0 && opciones[activa] !== undefined) { e.preventDefault(); elegir(opciones[activa]); }
        return;
      case 'Escape':
        if (abierto) { e.preventDefault(); e.stopPropagation(); cerrar(); }
        return;
      case 'Tab':
        if (abierto) cerrar();
        return;
      default:
    }
  }

  const idActiva = activa >= 0 && opciones[activa] !== undefined ? `${id}-op-${String(activa)}` : undefined;
  return (
    <div className="campo buscador" ref={raiz}>
      <label htmlFor={`${id}-input`}>{etiqueta}</label>
      <div className="buscador-control">
        <input
          id={`${id}-input`}
          type="text"
          role="combobox"
          aria-expanded={abierto}
          aria-controls={idLista}
          aria-autocomplete="list"
          aria-activedescendant={abierto ? idActiva : undefined}
          aria-invalid={error !== undefined}
          aria-describedby={error !== undefined ? idError : undefined}
          autoComplete="off"
          placeholder={placeholder}
          disabled={deshabilitado}
          value={consulta}
          onFocus={() => setAbierto(true)}
          onClick={() => setAbierto(true)}
          onChange={(ev) => { setConsulta(ev.target.value); setAbierto(true); if (valor !== null && ev.target.value === '') alElegir(null); }}
          onKeyDown={teclear}
        />
        {valor !== null && !deshabilitado && (
          <button type="button" className="pequeno buscador-limpiar" aria-label={`Quitar ${etiqueta.toLowerCase()}`}
            onClick={() => { alElegir(null); setConsulta(''); setAbierto(true); }}>×</button>
        )}
      </div>
      {abierto && (
        <ul className="buscador-lista" id={idLista} role="listbox" aria-label={etiqueta}>
          {cargando && opciones.length === 0 && <li className="buscador-aviso" aria-live="polite">Buscando…</li>}
          {!cargando && fallo && <li className="buscador-aviso" role="alert">No se ha podido buscar. Vuelve a intentarlo.</li>}
          {!cargando && !fallo && opciones.length === 0 && <li className="buscador-aviso">{sinResultados}</li>}
          {opciones.map((o, i) => (
            <li key={clave(o)} id={`${id}-op-${String(i)}`} role="option"
              aria-selected={valor !== null && clave(o) === clave(valor)}
              className={`buscador-opcion${i === activa ? ' activa' : ''}`}
              onMouseDown={(ev) => ev.preventDefault()}
              onMouseEnter={() => setActiva(i)}
              onClick={() => elegir(o)}>
              <span>{texto(o)}</span>
              {detalle !== undefined && <span className="suave pequena">{detalle(o)}</span>}
            </li>
          ))}
        </ul>
      )}
      {error !== undefined && <div className="error-campo" id={idError}>{error}</div>}
    </div>
  );
}
